import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import * as FiIcons from 'react-icons/fi';
import SafeIcon from '../../common/SafeIcon';

const { 
  FiX, FiBell, FiMail, FiCheck, FiClock, 
  FiCalendar, FiShield, FiPhone, FiHeart,
  FiStar, FiChevronRight, FiSettings, FiVolume2
} = FiIcons;

const MILESTONE_TYPES = [
  { id: 'heavenlyBirthday', label: 'Heavenly Birthday', icon: FiStar, color: 'text-accent-gold' },
  { id: 'weddingAnniversary', label: 'Wedding Anniversary', icon: FiHeart, color: 'text-rose-500' },
  { id: 'memorialAnniversary', label: 'Memorial Anniversary', icon: FiCalendar, color: 'text-accent-navy' },
];

const LEAD_TIMES = [
  { id: '1week', label: '1 Week Before' },
  { id: '3days', label: '3 Days Before' },
  { id: '1day', label: '1 Day Before' },
  { id: 'dayof', label: 'On the Day' },
];

const MilestoneNotificationModal = ({ isOpen, onClose, name, milestones }) => {
  const [selectedMilestones, setSelectedMilestones] = useState(['heavenlyBirthday', 'memorialAnniversary']);
  const [leadTimes, setLeadTimes] = useState(['1week', '1day']);
  const [channels, setChannels] = useState(['email']);
  const [email, setEmail] = useState('');
  const [phone, setPhone] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isSuccess, setIsSuccess] = useState(false);

  const toggleItem = (id, state, setter) => {
    setter(prev => prev.includes(id) ? prev.filter(i => i !== id) : [...prev, id]);
  };

  const toggleChannel = (id, state, setter) => {
    setter(prev => prev.includes(id) ? prev.filter(i => i !== id) : [...prev, id]);
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    setIsSubmitting(true);
    setTimeout(() => {
      setIsSubmitting(false);
      setIsSuccess(true);
      setTimeout(() => {
        setIsSuccess(false);
        onClose();
      }, 3000);
    }, 1500);
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-[160] flex items-center justify-center p-4 bg-accent-navy/60 backdrop-blur-md">
      <motion.div 
        initial={{ opacity: 0, scale: 0.95, y: 20 }}
        animate={{ opacity: 1, scale: 1, y: 0 }}
        className="bg-white rounded-[40px] shadow-2xl w-full max-w-2xl flex flex-col border border-brand-dark"
        style={{ maxHeight: 'calc(100vh - 2rem)' }}
      >
        <div className="p-8 md:p-12 border-b border-brand-light flex-shrink-0">
          <div className="flex justify-between items-start">
            <div className="flex items-center gap-4">
              <div className="w-14 h-14 bg-brand-light rounded-2xl flex items-center justify-center text-accent-gold shadow-sm border border-brand-dark">
                <SafeIcon icon={FiSettings} className="w-7 h-7" />
              </div>
              <div>
                <h3 className="text-3xl font-serif text-accent-navy">Legacy Reminders</h3>
                <p className="text-[10px] font-black uppercase tracking-widest text-text-muted">Honoring {name.split(' ')[0]} throughout the year</p>
              </div>
            </div>
            <button onClick={onClose} className="p-2 hover:bg-brand-light rounded-full transition-colors text-text-muted">
              <SafeIcon icon={FiX} className="w-6 h-6" />
            </button>
          </div>
        </div>

        <div className="p-8 md:p-12 overflow-y-auto flex-1">
          <AnimatePresence mode="wait">
            {!isSuccess ? (
              <motion.form 
                key="form"
                initial={{ opacity: 0 }} 
                animate={{ opacity: 1 }} 
                exit={{ opacity: 0 }}
                onSubmit={handleSubmit} 
                className="space-y-10"
              >
                {/* Milestone Type Selection */}
                <div className="space-y-4">
                  <label className="text-[10px] font-black uppercase tracking-widest text-accent-navy block">Track Milestones</label>
                  <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
                    {MILESTONE_TYPES.map((type) => (
                      <button 
                        key={type.id}
                        type="button"
                        disabled={!milestones[type.id]}
                        onClick={() => toggleItem(type.id, selectedMilestones, setSelectedMilestones)}
                        className={`flex flex-col items-center gap-3 p-5 rounded-3xl border-2 transition-all ${
                          selectedMilestones.includes(type.id) 
                            ? 'border-accent-gold bg-brand-light' 
                            : 'border-brand-dark opacity-40 grayscale'
                        } ${!milestones[type.id] ? 'hidden' : ''}`}
                      >
                        <SafeIcon icon={type.icon} className={`w-6 h-6 ${selectedMilestones.includes(type.id) ? type.color : ''}`} />
                        <span className="text-[9px] font-black uppercase tracking-widest text-center">{type.label}</span>
                      </button>
                    ))}
                  </div>
                </div>

                {/* Lead Time Selection */}
                <div className="space-y-4">
                  <label className="text-[10px] font-black uppercase tracking-widest text-accent-navy block">Lead Time Preference</label>
                  <div className="flex flex-wrap gap-2">
                    {LEAD_TIMES.map((time) => (
                      <button 
                        key={time.id}
                        type="button"
                        onClick={() => toggleItem(time.id, leadTimes, setLeadTimes)}
                        className={`px-5 py-3 rounded-xl text-[10px] font-black uppercase tracking-widest border-2 transition-all ${
                          leadTimes.includes(time.id) 
                            ? 'bg-accent-navy border-accent-navy text-white' 
                            : 'bg-white border-brand-dark text-text-muted hover:border-accent-gold'
                        }`}
                      >
                        {time.label}
                      </button>
                    ))}
                  </div>
                </div>

                {/* Contact Channels */}
                <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
                  <div className="space-y-4">
                    <label className="text-[10px] font-black uppercase tracking-widest text-accent-navy block">Delivery Methods</label>
                    <div className="space-y-3">
                      <button 
                        type="button" 
                        onClick={() => toggleChannel('email', channels, setChannels)}
                        className={`w-full flex items-center gap-4 p-4 rounded-2xl border-2 transition-all ${channels.includes('email') ? 'border-accent-gold bg-brand-light' : 'border-brand-dark opacity-60'}`}
                      >
                        <SafeIcon icon={FiMail} className={channels.includes('email') ? 'text-accent-gold' : ''} />
                        <span className="text-xs font-bold uppercase tracking-widest">Email Alerts</span>
                      </button>
                      <button 
                        type="button" 
                        onClick={() => toggleChannel('sms', channels, setChannels)}
                        className={`w-full flex items-center gap-4 p-4 rounded-2xl border-2 transition-all ${channels.includes('sms') ? 'border-accent-gold bg-brand-light' : 'border-brand-dark opacity-60'}`}
                      >
                        <SafeIcon icon={FiPhone} className={channels.includes('sms') ? 'text-accent-gold' : ''} />
                        <span className="text-xs font-bold uppercase tracking-widest">SMS Notifications</span>
                      </button>
                    </div>
                  </div>

                  <div className="space-y-4">
                    <label className="text-[10px] font-black uppercase tracking-widest text-accent-navy block">Contact Information</label>
                    <div className="space-y-3">
                      <div className="relative">
                        <SafeIcon icon={FiMail} className="absolute left-4 top-1/2 -translate-y-1/2 text-text-muted w-4 h-4" />
                        <input 
                          type="email" 
                          placeholder="Email for alerts"
                          disabled={!channels.includes('email')}
                          className="w-full pl-12 pr-4 py-4 bg-brand-light border-2 border-brand-dark rounded-2xl focus:border-accent-gold outline-none text-xs font-bold disabled:opacity-30 transition-all"
                          value={email}
                          onChange={(e) => setEmail(e.target.value)}
                        />
                      </div>
                      <div className="relative">
                        <SafeIcon icon={FiPhone} className="absolute left-4 top-1/2 -translate-y-1/2 text-text-muted w-4 h-4" />
                        <input 
                          type="tel" 
                          placeholder="Phone for SMS"
                          disabled={!channels.includes('sms')}
                          className="w-full pl-12 pr-4 py-4 bg-brand-light border-2 border-brand-dark rounded-2xl focus:border-accent-gold outline-none text-xs font-bold disabled:opacity-30 transition-all"
                          value={phone}
                          onChange={(e) => setPhone(e.target.value)}
                        />
                      </div>
                    </div>
                  </div>
                </div>

                <div className="bg-brand-light p-6 rounded-[32px] border border-brand-dark flex items-start gap-4">
                  <div className="w-10 h-10 bg-white rounded-xl flex items-center justify-center text-accent-gold shadow-sm flex-shrink-0">
                    <SafeIcon icon={FiVolume2} className="w-5 h-5" />
                  </div>
                  <p className="text-[11px] text-text-muted leading-relaxed italic">
                    Reminders are sent at 9:00 AM in your local timezone. You can also include personal notes in these legacy alerts to help you prepare specific tributes or gatherings.
                  </p>
                </div>

                <button 
                  type="submit" 
                  disabled={isSubmitting || channels.length === 0 || selectedMilestones.length === 0}
                  className="w-full btn-primary py-5 text-lg flex items-center justify-center gap-3 shadow-xl relative overflow-hidden group"
                >
                  <div className="absolute inset-0 bg-white/10 translate-y-full group-hover:translate-y-0 transition-transform duration-300" />
                  {isSubmitting ? (
                    <div className="w-6 h-6 border-2 border-white/30 border-t-white rounded-full animate-spin" />
                  ) : (
                    <>Save Notification Plan</>
                  )}
                </button>
              </motion.form>
            ) : (
              <motion.div key="success" initial={{ opacity: 0, scale: 0.9 }} animate={{ opacity: 1, scale: 1 }} className="py-16 text-center">
                <div className="w-28 h-28 bg-accent-gold text-white rounded-full flex items-center justify-center mx-auto mb-10 shadow-2xl shadow-accent-gold/20">
                  <SafeIcon icon={FiCheck} className="w-14 h-14" />
                </div>
                <h4 className="text-4xl font-serif text-accent-navy mb-4">Plan Configured</h4>
                <p className="text-text-muted font-light leading-relaxed max-w-sm mx-auto mb-10">
                  Your legacy reminder cycle is now active. We will notify you ahead of each selected milestone.
                </p>
                <button onClick={onClose} className="btn-secondary px-10">Return to Memorial</button>
              </motion.div>
            )}
          </AnimatePresence>
        </div>
        <div className="bg-brand-light p-6 flex justify-center items-center gap-2 text-[10px] font-black uppercase tracking-[0.2em] text-text-muted border-t border-brand-dark flex-shrink-0">
          <SafeIcon icon={FiShield} className="w-4 h-4 text-accent-gold" /> Protected Legacy Communications
        </div>
      </motion.div>
    </div>
  );
};

export default MilestoneNotificationModal;